import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../context/AppContext';
import { MessageSquare, Share2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn, formatTime } from '../utils';
import CommentSection from './CommentSection';

const getCategoryStyle = (category) => {
  if (!category) return 'text-slate-600 bg-slate-100 dark:text-slate-400 dark:bg-slate-500/10';
  if (category.includes('Spotted') || category.includes('Pyaar')) return 'text-fuchsia-600 bg-fuchsia-100 dark:text-fuchsia-400 dark:bg-fuchsia-500/10 border border-fuchsia-500/30';
  if (category.includes('Kalesh') || category.includes('Rant')) return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-500/10 border border-red-500/30';
  if (category.includes('Bakaaiti') || category.includes('Funny')) return 'text-amber-600 bg-amber-50 dark:text-yellow-400 dark:bg-yellow-500/10';
  if (category.includes('Night') || category.includes('Life')) return 'text-indigo-600 bg-indigo-50 dark:text-indigo-400 dark:bg-indigo-500/10';
  return 'text-slate-600 bg-slate-100 dark:text-slate-400 dark:bg-slate-500/10';
};

export default function PostCard({ post }) {
  const [showComments, setShowComments] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [reactions, setReactions] = useState(post.reactions || {});
  const [hasReacted, setHasReacted] = useState(false);
  const { addReaction, token, currentUser } = useContext(AppContext);
  const navigate = useNavigate();
  const isAdmin = currentUser?.username === 'admin';

  const handleReaction = (emoji) => {
    if (!token) {
       navigate('/login');
       return;
    }

    if (!hasReacted || isAdmin) {
      setReactions({ ...reactions, [emoji]: (reactions[emoji] || 0) + 1 });
      if (!isAdmin) setHasReacted(true);
      if (post.id) {
        addReaction(post.id, emoji);
      }
    }
  };

  const handleAutoCardShare = () => {
    // Create an offscreen canvas
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Premium square image dimensions for best aesthetic
    canvas.width = 1080;
    canvas.height = 1080;
    
    // Draw pure solid black background
    ctx.fillStyle = '#050505';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw premium colorful gradient border
    const borderGrad = ctx.createLinearGradient(0, 0, 1080, 1080);
    borderGrad.addColorStop(0, '#6366f1'); // Indigo accent
    borderGrad.addColorStop(1, '#d946ef'); // Fuchsia accent
    ctx.strokeStyle = borderGrad;
    ctx.lineWidth = 24;
    ctx.strokeRect(12, 12, 1080 - 24, 1080 - 24);

    // Separator line under title
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(80, 150);
    ctx.lineTo(1000, 150);
    ctx.stroke();

    // Title / Branding
    ctx.font = "italic 46px 'Inter', sans-serif";
    ctx.fillStyle = '#ffffff';
    ctx.fillText("SELFFO", 80, 110);

    // Category
    ctx.font = "bold 32px 'Inter', sans-serif";
    ctx.fillStyle = '#c084fc'; // light purple
    ctx.fillText(post.category?.toUpperCase() || 'GENERAL', 80, 215);

    // University
    ctx.font = "bold 28px 'Inter', sans-serif";
    ctx.fillStyle = '#64748b'; // slate
    ctx.fillText(`🚩 ${post.university || 'Anonymous University'}`, 80, 270);

    // Main Post Text Body (Wrapped text fallback)
    ctx.font = "normal 42px 'Inter', sans-serif";
    ctx.fillStyle = '#f8fafc';
    
    const maxTextWidth = 920;
    const x = 80;
    let y = 370;
    const lineHeight = 66;
    
    const words = post.text.split(' ');
    let line = '';
    for (let i = 0; i < words.length; i++) {
      const testLine = line + words[i] + ' ';
      const metrics = ctx.measureText(testLine);
      if (metrics.width > maxTextWidth && i > 0) {
        ctx.fillText(line, x, y);
        line = words[i] + ' ';
        y += lineHeight;
      } else {
        line = testLine;
      }
    }
    ctx.fillText(line, x, y);

    // Shared via text / Footer
    ctx.font = "italic 28px 'Inter', sans-serif";
    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.fillText("Shared via Selffo 🚀", 740, 1010);
    
    // Direct Image + Link share using Canvas blob
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      const file = new File([blob], `selffo_confession_${post.id || 'card'}.png`, { type: 'image/png' });
      const shareData = {
        title: 'Selffo Card Confession',
        text: `✨ New Confession on Selffo 🚀\nExplore more on Selffo!`,
        url: window.location.origin,
        files: [file]
      };

      if (navigator.canShare && navigator.canShare(shareData)) {
        try {
          await navigator.share(shareData);
        } catch (err) {
          // Manual download fallback
          const imageUrl = canvas.toDataURL('image/png');
          const link = document.createElement('a');
          link.href = imageUrl;
          link.download = `selffo_confession_${post.id || 'card'}.png`;
          link.click();
          alert('Image downloaded successfully! Your premium black card is ready to share.');
        }
      } else {
        // Fallback for browsers not supporting file sharing
        const imageUrl = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = imageUrl;
        link.download = `selffo_confession_${post.id || 'card'}.png`;
        link.click();
        alert('Image downloaded successfully! Your premium black card is ready to share.');
      }
    }, 'image/png');
  };

  const isKalesh = post.category?.includes('Kalesh') || post.comments > 5 || (post.reactions && post.reactions['Bawal 🔥'] > 5);
  const isSpotted = post.category?.includes('Spotted');

  // "See More" logic
  const isLongText = post.text.length > 300;
  const displayText = isExpanded || !isLongText ? post.text : post.text.slice(0, 300) + "...";

  return (
    <motion.div 
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 sm:p-5 bg-white dark:bg-selffo-dark border-b border-slate-100 dark:border-slate-900/50 transition-colors"
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex flex-col gap-1.5">
          <div className="flex flex-wrap gap-2 items-center">
            <span 
              className={cn(
                "text-[11px] font-bold px-2 py-0.5 rounded uppercase tracking-wider",
                getCategoryStyle(post.category)
              )}
            >
              {post.category}
            </span>
            <span className="text-xs text-slate-400 dark:text-slate-600 font-medium">
              {formatTime(post.timestamp)}
            </span>
          </div>
          <div className="flex items-center">
            <span className="text-[10px] font-bold text-slate-400 dark:text-slate-700 bg-slate-50 dark:bg-slate-900 px-1.5 py-0.5 rounded uppercase tracking-tighter">
              🚩 {post.university}
            </span>
          </div>
        </div>

        <button 
          onClick={handleAutoCardShare}
          className="text-slate-400 dark:text-slate-600 hover:text-slate-900 dark:hover:text-white transition-colors p-1"
        >
          <Share2 className="w-5 h-5" />
        </button>
      </div>

      <div className="mb-4">
        <p className="text-[17px] leading-relaxed text-slate-800 dark:text-slate-100 whitespace-pre-wrap tracking-[-0.01em]">
          {displayText}
        </p>
        {isLongText && (
          <button 
            onClick={() => setIsExpanded(!isExpanded)}
            className="mt-1 text-[13px] font-bold text-indigo-500 hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-300 transition-colors"
          >
            {isExpanded ? "Show Less" : "See More"}
          </button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-1 select-none">
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
          {Object.entries(reactions).map(([emoji, count]) => (
            <button
              key={emoji}
              onClick={() => handleReaction(emoji)}
              disabled={hasReacted && !isAdmin}
              className={cn(
                "flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-full text-[13px] font-medium transition-all duration-200 border",
                (hasReacted && !isAdmin)
                  ? "bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 opacity-80 cursor-default"
                  : "bg-transparent border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900 active:scale-95"
              )}
            >
              <span className="text-lg leading-none">{emoji.split(' ')[1]}</span>
              <span>{count}</span>
            </button>
          ))}

          <button 
            onClick={() => setShowComments(!showComments)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-medium text-slate-600 dark:text-slate-300 bg-transparent border border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900 transition-all"
          >
            <MessageSquare className="w-4 h-4" />
            <span>{post.comments || 0}</span>
          </button>
        </div>
      </div>

      <AnimatePresence>
        {showComments && <CommentSection comments={post.comments_list || []} postId={post.id} />}
      </AnimatePresence>
    </motion.div>
  );
}
